// Copied from the Toolkit (shared/ui/Dialog.tsx).
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

interface Props { title: ReactNode; onClose: () => void; wide?: boolean; children: ReactNode; actions?: ReactNode }

// A modal dialog: named by its heading, the focus moves into it (first field, else first control) and stays inside while Tab is
// pressed, Esc or a click on the backdrop closes it, and the focus goes back where it was afterwards.
export function Dialog({ title, onClose, wide = false, children, actions }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const headId = useId();
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const el = box.current;
    const visible = () => [...(el?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter((x) => x.offsetParent !== null || import.meta.env.MODE === 'test');
    const t = setTimeout(() => {
      if (el && !el.contains(document.activeElement)) (el.querySelector<HTMLElement>('input:not([type="hidden"]),select,textarea,[contenteditable="true"]') || visible()[0])?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      // only the dialog on top answers
      const all = document.querySelectorAll('.overlay .modal');
      if (all[all.length - 1] !== el) return;
      if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); }
      if (e.key === 'Tab') {
        const f = visible(); if (!f.length) return;
        const first = f[0]!; const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); if (opener && document.contains(opener)) opener.focus?.(); };
  }, []);

  return createPortal(
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={box} className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={headId}>
        <h3 id={headId}>{title}</h3>
        {children}
        {actions && <div className="actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}
